import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, Truck, Leaf, CreditCard, Clock, Ghost, Gift } from "lucide-react";
import Avis from "@/components/Avis";
import { SeasonDecor } from "@/components/SeasonDecor";
import { useConfig, eur, PHONE, PHONE_TEL } from "@/lib/api";
import { CITIES } from "@/data/cities";
import { METIERS } from "@/data/metiers";
import { getSeason } from "@/data/season";

const HERO_IMG = "/photos/maison-4.webp";
const LYON_IMG =
  "https://images.unsplash.com/photo-1612116158704-95c1c5b895e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxseW9uJTIwZnJhbmNlJTIwY2l0eXNjYXBlfGVufDB8fHx8MTc4NzE1MjQ0M3ww&ixlib=rb-4.1.0&q=85";
const DROME_IMG = "/photos/maison-2.webp";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[92vh] items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Table de brunch gourmande dressée avec soin"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-espresso/55" />
      </motion.div>
      <SeasonDecor />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-12">
        <MaskedLine delay={0.1}>
          <span className="eyebrow !text-cream/80">Traiteur brunch · Lyon & Drôme</span>
        </MaskedLine>
        <h1 className="display-hero mt-6 text-cream text-5xl sm:text-6xl lg:text-8xl">
          <MaskedLine delay={0.25}>Brunch à domicile à Lyon,</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-terracotta">Valence & Rhône-Alpes.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-cream/80 md:text-lg">
            Brunchs gourmands, petits-déjeuners et formules livrés directement chez vous,
            en Airbnb, en entreprise ou pour vos événements.{" "}
            <span className="font-semibold text-cream">Brunchs dès 23,90 €</span> · petits-déjeuners dès 9,90 € / personne —
            créneau bloqué dès le paiement, sans aucun appel.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/commander" data-testid="hero-order-cta" className="btn-primary">
            Commander mon brunch <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
          <Link to="/devis" data-testid="hero-devis-cta" className="btn-ghost !border-cream/40 !text-cream hover:!bg-cream hover:!text-espresso">
            Demander un devis
          </Link>
          <span data-testid="hero-halal-badge" className="rounded-full border border-cream/30 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream/85">
            100% halal · sans porc
          </span>
          {getSeason() && (() => { const s = getSeason(); const Icon = s.id === "halloween" ? Ghost : Gift; return (
            <Link to={s.path} data-testid="hero-season-chip" className="inline-flex items-center gap-2 rounded-full border border-terracotta/70 bg-terracotta/20 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream backdrop-blur-sm transition-colors duration-300 hover:bg-terracotta/40">
              <Icon size={14} strokeWidth={1.5} /> Menu {s.label} — jusqu'au {s.until}
            </Link>
          ); })()}
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-cream/60"
        >
          Livraison Lyon • Valence • Drôme • Rhône
        </motion.p>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = [
    "Viennoiseries artisanales", "Produits frais du matin", "Livraison premium",
    "Paiement sécurisé", "Formules sur mesure", "Lyon · Bron · Valence",
  ];
  return (
    <div data-testid="editorial-marquee" className="border-y border-sand bg-[#F1EAE0] py-6">
      <Marquee speed={30} gradient={false}>
        {words.map((w, i) => (
          <span key={i} className="mx-16 flex items-center gap-16">
            <span className="font-serif text-2xl font-light italic tracking-tight text-espresso/80">{w}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Formules() {
  const { config } = useConfig();
  if (!config) return null;
  const byId = Object.fromEntries(config.formules.map((f) => [f.id, f]));
  const { douceur, signature, desir, grazing } = byId;

  const Card = ({ f, span, tall }) => {
    if (!f) return null;
    const isDevis = !!f.devis;
    return (
    <Reveal className={span}>
      <Link
        to={isDevis ? "/devis?formule=grazing" : `/commander?formule=${f.id}`}
        data-testid={`formule-card-${f.id}`}
        className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white/50 transition-[transform,border-color] duration-500 hover:scale-[1.015] ${
          f.featured ? "border-terracotta/60" : "border-sand hover:border-terracotta/40"
        }`}
      >
        <div className={`relative overflow-hidden ${tall ? "h-72 lg:h-96" : "h-56"}`}>
          <img
            src={f.image}
            alt={f.nom}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {f.featured && (
            <span data-testid="formule-featured-badge" className="absolute right-4 top-4 rounded-full bg-terracotta px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream shadow-lg">
              Notre formule préférée
            </span>
          )}
          {isDevis && (
            <span data-testid="formule-devis-badge" className="absolute right-4 top-4 rounded-full bg-espresso/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream backdrop-blur">
              Sur devis
            </span>
          )}
          <div className="absolute left-4 top-4 rounded-full bg-cream/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-espresso backdrop-blur">
            {isDevis ? `dès ${eur(f.prix)} / pers.` : `${f.prefix || ""}${eur(f.prix)} / pers.`}
          </div>
        </div>
        <div className="flex flex-1 flex-col p-8">
          <h3 className="font-serif text-3xl font-light tracking-tight">{f.nom}</h3>
          <p className="mt-1 text-sm italic text-clay">{f.tagline}</p>
          <ul className="mt-5 space-y-2 text-sm leading-relaxed text-espresso/70">
            {f.items.slice(0, tall ? 9 : 5).map((it) => (
              <li key={it} className="flex gap-2">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-terracotta" />
                {it}
              </li>
            ))}
            {f.items.length > (tall ? 9 : 5) && (
              <li className="flex gap-2 italic text-clay">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-terracotta" />
                … et {f.items.length - (tall ? 9 : 5)} autres gourmandises
              </li>
            )}
          </ul>
          <span className="mt-auto inline-flex items-center gap-2 pt-6 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
            {isDevis ? "Demander un devis" : "Commander mon brunch"} <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </Reveal>
    );
  };

  return (
    <section id="formules" data-testid="formules-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
      <Reveal>
        <p className="eyebrow">Nos formules brunch</p>
        <h2 className="display-hero mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl">
          Trois brunchs, <span className="italic text-terracotta">une exigence.</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-12">
        <Card f={signature} span="md:col-span-7" tall />
        <div className="flex flex-col gap-8 md:col-span-5">
          <Card f={douceur} span="" />
          <Reveal className="flex-1">
            <Link
              to="/devis"
              data-testid="formule-card-surmesure"
              className="group flex h-full flex-col justify-between rounded-2xl bg-espresso p-8 text-cream transition-transform duration-500 hover:scale-[1.015]"
            >
              <div>
                <h3 className="font-serif text-3xl font-light tracking-tight">Sur mesure</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">
                  Une envie particulière, un régime spécifique, un événement d'exception ?
                  Nous composons votre formule idéale, pièce par pièce.
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
                Demander un devis <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
        <Card f={desir} span="md:col-span-6" />
        <Card f={grazing} span="md:col-span-6" />
      </div>
      <Reveal className="mt-8">
        <Link
          to="/livraison-petit-dejeuner"
          data-testid="formule-banner-petitdej"
          className="group flex flex-col gap-4 rounded-2xl border border-sand bg-white/60 p-8 transition-colors duration-300 hover:border-terracotta/50 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Nos formules petit-déjeuner</p>
            <p className="mt-2 font-serif text-2xl font-light">
              Continental Essentiel 9,90 € · Continental Gourmand 14,80 € <span className="italic text-clay">/ personne</span>
            </p>
            <p className="mt-1 text-sm text-clay">Viennoiseries pur beurre, pain artisanal, fruit frais — livrés dès 8h. Boissons chaudes et jus pressés en option.</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
            Découvrir <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </Link>
      </Reveal>
    </section>
  );
}

function Manifesto() {
  const chapters = [
    {
      n: "01", icon: Leaf, title: "Produits frais, chaque matin",
      text: "Viennoiseries de l'artisan, fruits de saison, œufs fermiers : nous sélectionnons chaque ingrédient avec la rigueur d'une maison traiteur.",
    },
    {
      n: "02", icon: Truck, title: "Une livraison premium",
      text: "Nous ne livrons pas un simple repas : nous apportons une expérience prête à déguster, dressée avec soin, à l'heure exacte de votre créneau.",
    },
    {
      n: "03", icon: CreditCard, title: "Réservation & paiement en ligne",
      text: "Formule, options, date, créneau, adresse : tout se règle en quelques minutes, par carte bancaire, en toute sécurité.",
    },
  ];
  return (
    <section data-testid="manifesto-section" className="border-y border-sand bg-[#F1EAE0]">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
        <Reveal>
          <p className="eyebrow">Le manifeste</p>
        </Reveal>
        <div className="mt-8 space-y-24">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05 * i}>
              <div className="grid gap-8 md:grid-cols-12 md:items-start">
                <div className="md:col-span-4">
                  <span className="font-serif text-7xl font-light tracking-tighter text-terracotta/60 lg:text-8xl">{c.n}</span>
                </div>
                <div className="md:col-span-8">
                  <c.icon size={28} strokeWidth={1.25} className="text-terracotta" />
                  <h3 className="mt-4 font-serif text-3xl font-light tracking-tight lg:text-4xl">{c.title}</h3>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-espresso/70">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Zones() {
  const zones = [
    {
      to: "/lyon", img: LYON_IMG, name: "Lyon · Métropole", base: "Atelier de Bron — PARC du Chêne",
      desc: "Lyon, Villeurbanne, Bron, Caluire et toute la métropole — et aussi Vienne (38), Trévoux et Bourg-en-Bresse (01), Mâcon et Chalon-sur-Saône (71) : votre brunch livré depuis notre atelier de Bron.",
      testid: "zone-card-lyon",
    },
    {
      to: "/drome", img: DROME_IMG, name: "Drôme · Valence", base: "Atelier de Valence — rue des Mourettes",
      desc: "Valence, Romans-sur-Isère, Bourg-de-Péage et toute la Drôme, jusqu'à Beaurepaire et Saint-Marcellin (38) : la même exigence, au cœur de la vallée du Rhône.",
      testid: "zone-card-drome",
    },
  ];
  return (
    <section data-testid="zones-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
      <Reveal>
        <p className="eyebrow">Deux ateliers — 69 · 26 · 01 · 71 · 38</p>
        <h2 className="display-hero mt-4 text-4xl sm:text-5xl lg:text-6xl">
          Proches de vous, <span className="italic text-terracotta">partout.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-8 md:grid-cols-2">
        {zones.map((z, i) => (
          <Reveal key={z.to} delay={0.1 * i}>
            <Link to={z.to} data-testid={z.testid} className="group block overflow-hidden rounded-2xl border border-sand">
              <div className="relative h-80 overflow-hidden lg:h-96">
                <img src={z.img} alt={z.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/70">{z.base}</p>
                  <h3 className="mt-2 font-serif text-4xl font-light tracking-tight text-cream">{z.name}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/80">{z.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
                    Découvrir la zone <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2} className="mt-12">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-clay">Toutes nos villes de livraison</p>
        <div className="mt-5 flex flex-wrap gap-2.5" data-testid="home-cities-links">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/brunch-a-domicile/${c.slug}`}
              data-testid={`home-city-link-${c.slug}`}
              className="rounded-full border border-sand bg-white/50 px-4 py-2 text-xs text-espresso/75 transition-colors duration-300 hover:border-terracotta/50 hover:text-espresso"
            >
              Brunch à {c.name}
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    "Choisissez votre formule et vos options",
    "Indiquez le nombre de convives",
    "Sélectionnez la date et le créneau de livraison",
    "Renseignez votre adresse — les frais se calculent seuls",
    "Réglez en ligne par carte bancaire",
    "Nous livrons, vous dégustez",
  ];
  return (
    <section data-testid="how-section" className="border-y border-sand bg-espresso text-cream">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
        <Reveal>
          <p className="eyebrow">Simple comme bonjour</p>
          <h2 className="display-hero mt-4 max-w-2xl text-4xl sm:text-5xl lg:text-6xl">
            Six gestes, <span className="italic text-terracotta">zéro effort.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-cream/10 bg-cream/10 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <div className="flex h-full flex-col gap-6 bg-espresso p-8">
                <span className="font-serif text-5xl font-light text-terracotta/70">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm leading-relaxed text-cream/80">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <div className="flex flex-col items-start gap-6 rounded-2xl border border-terracotta/40 bg-terracotta/10 p-8 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <Clock size={24} strokeWidth={1.5} className="mt-1 shrink-0 text-terracotta" />
              <p className="max-w-2xl text-sm leading-relaxed text-cream/85">
                <strong className="text-cream">Commandez la veille pour le lendemain.</strong>{" "}
                Dès votre paiement validé, votre réservation est définitive — aucun appel nécessaire.
                Une envie de dernière minute ? Pour le jour J, c'est possible : contactez-nous
                directement au{" "}
                <a href={PHONE_TEL} data-testid="how-phone-link" className="font-semibold text-terracotta underline underline-offset-4">{PHONE}</a>.
              </p>
            </div>
            <Link to="/commander" data-testid="how-order-cta" className="btn-primary shrink-0">
              Commander mon brunch <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SeoText() {
  return (
    <section data-testid="seo-text-section" className="border-t border-sand mx-auto max-w-7xl px-6 py-24 lg:px-12">
      <Reveal>
        <p className="eyebrow">Le service</p>
        <h2 className="display-hero mt-4 max-w-3xl text-4xl sm:text-5xl">
          Brunch à domicile à Lyon <span className="italic text-terracotta">et dans la Drôme</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-12 md:grid-cols-3">
        <Reveal>
          <h3 className="font-serif text-2xl font-light">Un vrai service traiteur, chez vous</h3>
          <p className="mt-4 text-sm leading-relaxed text-espresso/70">
            Gourmandise Traiteur livre des brunchs faits maison à domicile, au bureau ou sur votre
            lieu d'événement : viennoiseries pur beurre, œufs brouillés crémeux, avocado toasts,
            pancakes moelleux, fruits frais de saison et jus pressés. Toutes nos formules sont 100%
            halal et sans porc, avec des alternatives volaille. Vous choisissez la formule, le créneau
            et l'adresse, vous réglez en ligne — nous livrons à l'heure exacte, dès 8h du matin.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="font-serif text-2xl font-light">Lyon & sa métropole</h3>
          <p className="mt-4 text-sm leading-relaxed text-espresso/70">
            Depuis notre atelier du PARC du Chêne à Bron, nous livrons Lyon (Presqu'île, Part-Dieu,
            Croix-Rousse, Confluence, Vieux Lyon), Villeurbanne, Caluire-et-Cuire, Vaulx-en-Velin,
            Décines-Charpieu, Saint-Priest, Chassieu, Vénissieux, Tassin-la-Demi-Lune, Écully, Oullins
            et l'ensemble de la métropole.{" "}
            <Link to="/lyon" data-testid="seo-link-lyon" className="font-semibold text-terracotta underline underline-offset-4">
              Découvrir le brunch à Lyon
            </Link>
            .
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <h3 className="font-serif text-2xl font-light">Valence & la Drôme</h3>
          <p className="mt-4 text-sm leading-relaxed text-espresso/70">
            Depuis notre atelier de la rue des Mourettes à Valence, nous parcourons la vallée du
            Rhône : Valence, Guilherand-Granges, Bourg-lès-Valence, Saint-Péray, Portes-lès-Valence,
            Alixan, Chabeuil, Livron-sur-Drôme, Tain-l'Hermitage, Romans-sur-Isère, Bourg-de-Péage,
            Tournon-sur-Rhône, Loriol-sur-Drôme, Crest et jusqu'à Montélimar.{" "}
            <Link to="/drome" data-testid="seo-link-drome" className="font-semibold text-terracotta underline underline-offset-4">
              Découvrir le brunch dans la Drôme
            </Link>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function SeasonSection() {
  const season = getSeason();
  if (!season) return null;
  const menu = METIERS[season.menuKey].menu;
  const Icon = season.id === "halloween" ? Ghost : Gift;
  return (
    <section data-testid="home-season-section" className="relative overflow-hidden bg-espresso text-cream">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-terracotta/25 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#7a4b1e]/20 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
        <Reveal>
          <p className="eyebrow !text-terracotta">
            <Icon size={13} strokeWidth={1.5} className="mr-2 inline -translate-y-px" />
            Édition limitée — jusqu'au {season.until}
          </p>
          <h2 className="display-hero mt-5 text-3xl sm:text-5xl">
            {season.heading[0]} <span className="italic text-terracotta">{season.heading[1]}</span>{season.heading[2]}
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/75">{season.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={season.path} data-testid="home-season-menu-cta" className="btn-primary">
              Découvrir le menu <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/commander" data-testid="home-season-order-cta" className="btn-ghost !border-cream/40 !text-cream hover:!bg-cream hover:!text-espresso">
              Commander mon brunch
            </Link>
          </div>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">{season.priceNote}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-cream/15 bg-cream/5 p-8 backdrop-blur-sm md:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">{menu.title}</p>
            <div className="mt-6">
              {menu.items.map((it, i) => (
                <div key={i} className={`flex items-baseline gap-4 py-3.5 ${i > 0 ? "border-t border-cream/10" : ""}`} data-testid={`home-season-item-${i}`}>
                  <span className="font-serif text-lg font-light">{it.name}</span>
                  <span className="hidden flex-1 border-b border-dotted border-cream/25 sm:block" />
                  <span className="hidden text-xs text-cream/55 sm:block sm:text-right">{it.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <Formules />
      <SeasonSection />
      <Avis />
      <SeoText />
      <Manifesto />
      <Zones />
      <HowItWorks />
    </>
  );
}
